import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Label,
} from "recharts";
import AppColors from "../../../Utils/AppColor";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../../ContextAPI/WasteManagementContext";
import { BinInfo, ChartDataPoint, Position } from "../../../Models/Models";
import { ScrollableDialogContent } from "../../../Utils/CommonStyles";

import {
  GetBinDetails,
  GetDLCDetails,
  GetDLCLastTenDaysChartData,
} from "../../../ApiServices/ApiRequests";
import { googleMapsConfig } from "../../../ApiServices/UrlConfigs";
import Loading from "../../../Controller/LoadingController/Loading";
import getDLCIconDataUrl from "../../../Assests/SVG/DLCIcon";
import { useMasterData } from "../../../ContextAPI/MasterContext";
import ImageNotFound from "../../../Assests/ImageNotFound.png";
const initialMapCenter = {
  lat: 1.3179623906210907,
  lng: 103.80952185215926,
};

const DLCPlot = () => {
  const { t } = useTranslation();
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value:
        | string
        | boolean
        | BinInfo[]
        | BinInfo
        | ChartDataPoint[]
        | number
        | null
        | google.maps.Map
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkChange = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsConfig.apiKey,
  });
  useEffect(() => {
    if (!wasteManagementState.showDLCChart) {
      handleInputChange("DLCChartData", []);
    }
  }, [wasteManagementState.showDLCChart]);

  useEffect(() => {
    if (masterState.selectedSiteCode.length > 0)
      fetchPlottingDLCData(
        masterState.selectedSiteCode,
        masterState.selectedGroupCode ?? ""
      );
  }, [masterState.selectedSiteCode, masterState.selectedGroupCode]);

  const fetchPlottingDLCData = async (
    siteCode: string,
    groupCode: string
  ): Promise<void> => {
    try {
      handleInputChange("isDLCPlotingOnMapLoad", true);
      const data: BinInfo[] = await GetDLCDetails(siteCode, groupCode);

      handleInputChange("DLCData", data);
    } catch (error) {
      console.error("Error  while loading Plotting Bin data", error);
    } finally {
      handleInputChange("isDLCPlotingOnMapLoad", false);
    }
  };

  const setMapBounds = useCallback(() => {
    if (!wasteManagementState?.map || wasteManagementState.DLCData.length === 0)
      return;

    const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

    wasteManagementState.DLCData.forEach((bin) => {
      const position: Position = {
        lat: parseFloat(bin.latitude),
        lng: parseFloat(bin.longitude),
      };
      bounds.extend(position);
    });

    wasteManagementState?.map.fitBounds(bounds);
  }, [
    wasteManagementState.map,
    wasteManagementState.DLCData,
    masterState.selectedSiteCode,
    masterState.selectedGroupCode,
  ]);

  useEffect(() => {
    if (wasteManagementState.map && wasteManagementState.DLCData.length > 0) {
      setMapBounds();
    }
  }, [
    wasteManagementState.map,
    wasteManagementState.DLCData,
    setMapBounds,
    masterState.selectedSiteCode,
  ]);

  const handleToggleChart = async (
    siteCode: string,
    deviceCode: string
  ): Promise<void> => {
    const newShowChartState: boolean = !wasteManagementState.showDLCChart;

    handleInputChange("showDLCChart", newShowChartState);

    if (newShowChartState) {
      try {
        handleInputChange("isDLCChartLoad", true);
        const data: ChartDataPoint[] = await GetDLCLastTenDaysChartData(
          siteCode,
          deviceCode
        );
        handleInputChange("DLCChartData", data);
      } catch (error) {
        console.error("Error while loading DIC Fullness Chart data", error);
      } finally {
        handleInputChange("isDLCChartLoad", false);
      }
    }
  };

  const handleMarkerClick = (SelectedDLCData: BinInfo): void => {
    handleInputChange("showDLCChart", false);
    handleInputChange("selectedDLC", SelectedDLCData);
  };

  const handleCloseModal = () => {
    handleInputChange("showDLCChart", false);
    handleInputChange("selectedDLC", null);
  };

  if (!isLoaded)
    return <Loading loading={true} type={`glass`} backDrop={false} />;
  const maxValue: number =
    Math.ceil(
      Math.max(
        ...wasteManagementState.DLCChartData?.map((item) => item.value)
      ) / 10
    ) * 10;

  const formatYAxisTick = (value: number) => `${value}%`;
  const DLCDetailsPopup = () => {
    if (!wasteManagementState.selectedDLC) {
      return null;
    }
    return (
      <Dialog
        open={!!wasteManagementState.selectedDLC}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <IconButton
          style={{ position: "absolute", top: "5px", right: "5px" }}
          onClick={handleCloseModal}
        >
          <CloseOutlinedIcon sx={{ color: AppColors.appWhite }} />
        </IconButton>

        <DialogTitle
          sx={{
            backgroundColor: AppColors.purple,
            color: AppColors.appWhite,
          }}
        >
          {wasteManagementState.selectedDLC.deviceName}
          <Box
            sx={{
              backgroundColor: "#7143c7",
              display: "inline",
              p: 1,
              borderRadius: 1,
              marginLeft: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" textAlign="center">
              {t("global.status")}:{wasteManagementState.selectedDLC.status}
            </Typography>
          </Box>
        </DialogTitle>

        <ScrollableDialogContent sx={{ marginTop: 2 }}>
          <Grid2 container flexDirection="row" spacing={2}>
            {/* Card 1: Bin Details */}
            <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Grid2 container spacing={1}>
                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.battery")}
                      </Typography>
                      <Typography variant="body2">
                        {wasteManagementState.selectedDLC.battery}%
                      </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.latitude")}
                      </Typography>
                      <Typography variant="body2">
                        {wasteManagementState.selectedDLC.latitude}
                      </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.longitude")}
                      </Typography>
                      <Typography variant="body2">
                        {wasteManagementState.selectedDLC.longitude}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.level")}
                      </Typography>
                      <Typography variant="body2">
                        {wasteManagementState.selectedDLC.level}%
                      </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.lastUpdate")}
                      </Typography>
                      <Typography variant="body2">
                        {wasteManagementState.selectedDLC.modifiedDateTime}
                      </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 6, md: 6, lg: 6 }}>
                      <Typography variant="body2" color={AppColors.textGray}>
                        {t("global.address")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordWrap: "break-word" }}
                      >
                        {wasteManagementState.selectedDLC.address}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </CardContent>
              </Card>
            </Grid2>

            {/* Card 2: Bin Image */}
            <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
              <Card sx={{ height: "100%" }}>
                {/* <CardMedia
                  component="img"
                  image={wasteManagementState.selectedDLC.image}
                  alt="Bin image"
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: 260,
                    objectFit: "cover",
                  }}
                /> */}

                <img
                  src={
                    wasteManagementState.selectedDLC.image
                      ? wasteManagementState.selectedDLC.image
                      : ImageNotFound
                  }
                  alt="DIC image"
                  style={{
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "contain",
                    minWidth: "100%", // Optional
                    display: "block", // Avoid inline-block spacing issues
                  }}
                />
              </Card>
            </Grid2>
          </Grid2>

          <Grid2 container justifyContent="center" marginTop={2}>
            <Button
              variant="outlined"
              onClick={() =>
                handleToggleChart(
                  masterState.selectedSiteCode,
                  wasteManagementState.selectedDLC?.deviceCode ?? ""
                )
              }
              startIcon={
                wasteManagementState.showDLCChart ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              }
            >
              {wasteManagementState.showDLCChart
                ? t("global.hideChart")
                : t("global.showChart")}
            </Button>
          </Grid2>

          <Loading
            loading={wasteManagementState.isDLCChartLoad}
            type={`dots`}
            backDrop={false}
          />

          {/* Chart Section */}
          {wasteManagementState.showDLCChart && (
            <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
              <Card>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", marginBottom: 1 }}
                >
                  {t("global.levelLast10Days")}
                </Typography>
                <Box
                  style={{
                    width: "100%",
                    height: 200,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={wasteManagementState.DLCChartData}
                      margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                    >
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis
                        dataKey="value"
                        tick={{ fontSize: 12 }}
                        tickFormatter={formatYAxisTick}
                        ticks={Array.from(
                          { length: maxValue / 10 + 1 },
                          (_, i) => i * 10
                        )}
                      >
                        <Label
                          value="Level in %"
                          angle={-90}
                          position="insideLeft"
                        />
                      </YAxis>
                      <Tooltip formatter={(value) => `${value} %`} />

                      <Bar
                        dataKey="value"
                        fill={AppColors.purple}
                        barSize={10}
                        background={{ fill: "#eee" }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid2>
          )}
        </ScrollableDialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3, border: "1px solid #eee" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "400px" }}
        center={wasteManagementState?.binPlottingmapCenter ?? undefined}
        zoom={12}
        key={`${masterState.selectedSiteCode}-${wasteManagementState.DLCData.length}`}
        onLoad={(mapInstance) => handleInputChange("map", mapInstance)}
      >
        {wasteManagementState.DLCData?.map((bin) => {
          console.log("DlcData", wasteManagementState.DLCData, bin);
          return (
            <Marker
              key={`${bin.deviceCode}-${bin.deviceName}`}
              position={{
                lat: parseFloat(bin.latitude),
                lng: parseFloat(bin.longitude),
              }}
              onClick={() => handleMarkerClick(bin)}
              icon={{
                url: getDLCIconDataUrl(bin.color),
                scaledSize: new google.maps.Size(40, 40),
              }}
            />
          );
        })}
        {DLCDetailsPopup()}
        <Loading
          loading={wasteManagementState.isDLCPlotingOnMapLoad}
          type={`dots`}
          backDrop={false}
        />
      </GoogleMap>
    </Box>
  );
};

export default DLCPlot;
